import React, { useMemo } from "react";
import { MapPin } from "lucide-react";

// Coordinate approssimative delle province italiane sulla mappa
// (x, y in unità SVG su viewBox 100x140)
const PROVINCE_COORDS = {
  // Nord
  MI: { x: 38, y: 26, regione: "Lombardia" }, TO: { x: 24, y: 30, regione: "Piemonte" },
  GE: { x: 32, y: 38, regione: "Liguria" }, VE: { x: 56, y: 25, regione: "Veneto" },
  BO: { x: 48, y: 38, regione: "Emilia-Romagna" }, TS: { x: 67, y: 22, regione: "Friuli-VG" },
  // Centro
  FI: { x: 47, y: 47, regione: "Toscana" }, PI: { x: 43, y: 50, regione: "Toscana" },
  RM: { x: 53, y: 65, regione: "Lazio" }, AN: { x: 58, y: 51, regione: "Marche" },
  PG: { x: 53, y: 55, regione: "Umbria" }, AQ: { x: 58, y: 60, regione: "Abruzzo" },
  // Sud
  NA: { x: 60, y: 75, regione: "Campania" }, BA: { x: 71, y: 73, regione: "Puglia" },
  CB: { x: 64, y: 68, regione: "Molise" }, PZ: { x: 70, y: 79, regione: "Basilicata" },
  CZ: { x: 73, y: 90, regione: "Calabria" },
  // Isole
  PA: { x: 56, y: 110, regione: "Sicilia" }, CT: { x: 67, y: 113, regione: "Sicilia" },
  RG: { x: 63, y: 121, regione: "Sicilia" }, CA: { x: 30, y: 105, regione: "Sardegna" },
};

// Path SVG semplificato dell'Italia (stylized)
const ITALY_PATHS = {
  // Italia continentale (nord-sud)
  mainland: "M 32 18 L 48 16 L 60 18 L 68 22 L 70 28 L 65 32 L 58 30 L 50 32 L 45 38 L 50 44 L 55 50 L 52 56 L 54 62 L 50 68 L 56 72 L 64 70 L 70 72 L 76 78 L 78 84 L 74 90 L 70 92 L 68 96 L 72 98 L 70 102 L 76 100 L 74 96 L 78 92 L 76 86 L 80 80 L 78 74 L 70 68 L 62 64 L 58 56 L 60 48 L 55 42 L 50 36 L 42 32 L 36 28 L 30 26 Z",
  // Sicilia
  sicilia: "M 48 108 L 55 105 L 64 106 L 72 110 L 70 116 L 65 122 L 58 124 L 52 122 L 46 118 L 46 112 Z",
  // Sardegna
  sardegna: "M 24 92 L 32 90 L 36 95 L 36 105 L 32 112 L 26 110 L 22 102 Z",
};

export default function ItalyMap({ iscritti, activeProvince, onSelectProvince }) {
  const countByProvincia = useMemo(() => {
    const map = {};
    iscritti.forEach((i) => {
      if (i.provincia) {
        map[i.provincia] = (map[i.provincia] || 0) + 1;
      }
    });
    return map;
  }, [iscritti]);

  const dotsPresenti = Object.keys(countByProvincia)
    .filter((p) => PROVINCE_COORDS[p])
    .map((p) => ({ code: p, ...PROVINCE_COORDS[p], count: countByProvincia[p] }));

  // Province totali da mostrare (anche se 0): usa quelle con dati
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-6 lg:p-8">
      <div className="flex items-start justify-between gap-4 mb-5">
        <div>
          <h3 className="text-lg font-extrabold text-[#0a1f55]">Distribuzione geografica</h3>
          <p className="text-[12.5px] text-slate-500 mt-0.5">Clicca su un puntino per filtrare gli iscritti</p>
        </div>
        {activeProvince && (
          <button
            data-testid="map-clear-filter"
            onClick={() => onSelectProvince(null)}
            className="text-[11.5px] font-bold text-[#102a6b] hover:underline"
          >
            Mostra tutti
          </button>
        )}
      </div>

      <div className="grid lg:grid-cols-5 gap-6 items-center">
        {/* SVG Map */}
        <div className="lg:col-span-3 flex justify-center">
          <svg viewBox="0 0 100 140" className="w-full max-w-md h-auto" aria-label="Mappa Italia">
            {/* Mainland */}
            <path
              d={ITALY_PATHS.mainland}
              fill="#e2e8f0"
              stroke="#94a3b8"
              strokeWidth="0.4"
              strokeLinejoin="round"
            />
            {/* Sicilia */}
            <path
              d={ITALY_PATHS.sicilia}
              fill="#e2e8f0"
              stroke="#94a3b8"
              strokeWidth="0.4"
              strokeLinejoin="round"
            />
            {/* Sardegna */}
            <path
              d={ITALY_PATHS.sardegna}
              fill="#e2e8f0"
              stroke="#94a3b8"
              strokeWidth="0.4"
              strokeLinejoin="round"
            />

            {/* Dots per provincia con iscritti */}
            {dotsPresenti.map((d) => {
              const isActive = activeProvince === d.code;
              const r = 1.5 + Math.min(d.count, 5) * 0.6;
              return (
                <g key={d.code}>
                  {/* Halo pulsante */}
                  <circle cx={d.x} cy={d.y} r={r + 1.5} fill={isActive ? "#f59e0b" : "#102a6b"} opacity="0.18">
                    <animate attributeName="r" values={`${r + 1.5};${r + 3};${r + 1.5}`} dur="2s" repeatCount="indefinite" />
                  </circle>
                  {/* Dot principale - cliccabile */}
                  <circle
                    cx={d.x}
                    cy={d.y}
                    r={r}
                    fill={isActive ? "#f59e0b" : "#102a6b"}
                    stroke="#fff"
                    strokeWidth="0.4"
                    style={{ cursor: "pointer" }}
                    onClick={() => onSelectProvince(isActive ? null : d.code)}
                    data-testid={`map-dot-${d.code}`}
                  >
                    <title>{d.code} ({d.regione}) — {d.count} iscritt{d.count === 1 ? "o" : "i"}</title>
                  </circle>
                  {/* Label sigla */}
                  <text
                    x={d.x}
                    y={d.y - r - 1.5}
                    textAnchor="middle"
                    fontSize="2.4"
                    fontWeight="800"
                    fill={isActive ? "#b45309" : "#0a1f55"}
                    style={{ cursor: "pointer", userSelect: "none" }}
                    onClick={() => onSelectProvince(isActive ? null : d.code)}
                  >
                    {d.code}
                  </text>
                </g>
              );
            })}
          </svg>
        </div>

        {/* Sidebar province */}
        <div className="lg:col-span-2 space-y-2">
          <div className="text-[10.5px] uppercase tracking-wider font-extrabold text-slate-500 mb-2">
            Province attive ({dotsPresenti.length})
          </div>
          {dotsPresenti
            .sort((a, b) => b.count - a.count)
            .map((d) => {
              const isActive = activeProvince === d.code;
              return (
                <button
                  key={d.code}
                  data-testid={`map-pill-${d.code}`}
                  onClick={() => onSelectProvince(isActive ? null : d.code)}
                  className={`w-full flex items-center justify-between gap-3 px-4 py-2.5 rounded-xl border transition-all ${
                    isActive
                      ? "bg-[#f59e0b] border-[#f59e0b] text-[#0a1020]"
                      : "bg-white border-slate-200 text-[#0a1f55] hover:border-[#102a6b]/40"
                  }`}
                >
                  <div className="flex items-center gap-2.5">
                    <MapPin className={`h-4 w-4 ${isActive ? "text-[#0a1020]" : "text-[#102a6b]"}`} />
                    <div className="text-left">
                      <div className="font-extrabold text-[13.5px]">{d.code}</div>
                      <div className={`text-[10.5px] ${isActive ? "text-[#7c2d12]" : "text-slate-500"}`}>{d.regione}</div>
                    </div>
                  </div>
                  <span className={`text-[12px] font-extrabold px-2 py-0.5 rounded-full ${
                    isActive ? "bg-[#0a1020] text-[#fbbf24]" : "bg-[#102a6b]/10 text-[#102a6b]"
                  }`}>{d.count}</span>
                </button>
              );
            })}
          {dotsPresenti.length === 0 && (
            <div className="text-[12.5px] text-slate-400 italic">Nessuna provincia con iscritti</div>
          )}
        </div>
      </div>
    </div>
  );
}
